import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, ArrowUpRight, Instagram, Phone, Mail } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import { NAV_LINKS, COMPANY, LOGO_URL } from "../data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = () => setScrolled(window.scrollY > 30);
    handler();
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 bg-white transition-shadow duration-300 ${
        scrolled
          ? "border-b border-neutral-200 shadow-sm"
          : "border-b border-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 flex items-center justify-between h-16 md:h-20">
        <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-2 md:gap-3 group min-w-0">
          <img
            src={LOGO_URL}
            alt="Fratelli D'Ambrosio S.R.L."
            className="h-10 md:h-12 w-auto object-contain transition-transform group-hover:scale-105 flex-shrink-0 mix-blend-multiply"
          />
          <div className="flex flex-col leading-tight min-w-0">
            <span className="font-display font-black tracking-tighter text-[11px] sm:text-[13px] md:text-[15px] uppercase text-[#1D4ED8] truncate">
              {COMPANY.name}
            </span>
            <span className="font-mono text-[9px] sm:text-[10px] text-neutral-500 tracking-[0.2em] uppercase">
              S.R.L. · dal 1970
            </span>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.to.replace("/", "") || "home"}`}
              className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link
            to="/contatti"
            data-testid="nav-cta-preventivo"
            className="hidden md:inline-flex items-center gap-2 bg-[#1D4ED8] hover:bg-[#1E3A8A] text-white px-5 py-3 rounded-full font-display font-bold text-[13px] tracking-wider uppercase transition-colors"
          >
            Richiedi Preventivo
            <ArrowUpRight className="w-4 h-4" />
          </Link>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="nav-mobile-menu-trigger"
                className="lg:hidden w-11 h-11 rounded-full border border-neutral-300 flex items-center justify-center hover:bg-[#0B1E3F] hover:text-white transition-colors"
                aria-label="Apri menu"
              >
                <Menu className="w-5 h-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#0B1E3F] text-white border-l-0 w-[85vw] sm:w-[400px] p-0">
              <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
              <SheetDescription className="sr-only">
                Navigazione principale del sito
              </SheetDescription>
              <div className="p-8 pt-14 h-full flex flex-col">
                <div className="mb-10 flex items-center gap-4">
                  <img src={LOGO_URL} alt="Fratelli D'Ambrosio" className="h-14 w-auto object-contain brightness-0 invert" />
                  <div>
                    <div className="font-display font-black text-xl uppercase tracking-tighter">
                      Fratelli<br />D'Ambrosio
                    </div>
                    <div className="font-mono text-[10px] tracking-[0.2em] text-neutral-400 uppercase mt-1">
                      S.R.L. · dal 1970
                    </div>
                  </div>
                </div>
                <nav className="flex flex-col gap-1">
                  {NAV_LINKS.map((l, i) => (
                    <Link
                      key={l.to}
                      to={l.to}
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${l.to.replace("/", "") || "home"}`}
                      className="group flex items-center justify-between py-4 border-b border-white/10 font-display font-bold text-xl uppercase tracking-tight hover:text-[#60A5FA] transition-colors"
                    >
                      <span>
                        <span className="font-mono text-[10px] text-neutral-500 mr-3">
                          0{i + 1}
                        </span>
                        {l.label}
                      </span>
                      <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>
                  ))}
                </nav>
                <div className="mt-auto pt-8 space-y-3 text-sm text-neutral-400 border-t border-white/10">
                  <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center gap-3 hover:text-white">
                    <Phone className="w-4 h-4 text-[#60A5FA]" />
                    {COMPANY.phone}
                  </a>
                  <a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 hover:text-white break-all">
                    <Mail className="w-4 h-4 text-[#60A5FA]" />
                    {COMPANY.email}
                  </a>
                  <a
                    href={COMPANY.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 hover:text-white"
                  >
                    <Instagram className="w-4 h-4 text-[#60A5FA]" />
                    {COMPANY.instagramHandle}
                  </a>
                  <div className="pt-2 text-xs text-neutral-500">{COMPANY.address}</div>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
